import React, { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../lib/data";
import { Reveal, Overline } from "./Reveal";

function ServiceCard({ s, i }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${-y * 6}deg) rotateY(${x * 6}deg) translateY(-6px)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
  };
  const Icon = s.icon;

  return (
    <Reveal delay={i * 0.06}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        data-testid={`service-card-${s.id}`}
        className="group relative h-full rounded-2xl glass overflow-hidden transition-[transform,border-color] duration-300 ease-out hover:border-brand/50"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="relative h-36 overflow-hidden">
          <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, var(--surface), transparent)" }} />
          <div className="absolute top-4 left-4 grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-brand to-secondary glow-brand">
            <Icon size={20} className="text-[#05050A]" />
          </div>
        </div>
        <div className="p-6">
          <p className="text-xs font-mono uppercase tracking-[0.15em] text-brand mb-2">{s.tagline}</p>
          <h3 className="font-heading text-2xl font-semibold tracking-tight">{s.title}</h3>
          <p className="mt-3 text-sm text-textsub leading-relaxed">{s.desc}</p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {s.points.map((p) => (
              <li key={p} className="text-xs rounded-full border border-line px-3 py-1 text-textsub">{p}</li>
            ))}
          </ul>
          <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-textmain group-hover:text-brand transition-colors">
            Learn more <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services" className="relative py-12 md:py-16 noise">
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12">
        <Reveal>
          <Overline>What we engineer</Overline>
          <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight max-w-2xl leading-tight">
              End-to-end capability across the <span className="text-gradient">modern stack</span>
            </h2>
            <p className="text-textsub max-w-md">
              Six deeply-integrated practices, one accountable partner — from cloud foundations to AI at the edge of your product.
            </p>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
